import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { MdDelete, MdSave } from "react-icons/md";
import { FaArrowLeft } from "react-icons/fa";
import { PDFViewer, PDFDownloadLink } from "@react-pdf/renderer";
import PresupuestoPDF from "./PresupuestoPDF";
import logoTwinpackSrc from "../assets/logo-rosa.png";

const BASE = process.env.REACT_APP_API_BASE || "http://localhost/twinpack/php";

// ─── Estilos ──────────────────────────────────────────────────────────────────
const S = {
  page: {
    minHeight: "100vh",
    background: "#f7f4f6",
    padding: "24px 16px",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  topBar: {
    maxWidth: 1340,
    margin: "0 auto 20px",
    display: "flex",
    alignItems: "center",
    gap: 14,
  },
  backBtn: {
    background: "none", border: "none", cursor: "pointer",
    color: "#9f0059", display: "flex", alignItems: "center",
    padding: 6, borderRadius: 8,
  },
  title: { fontSize: 22, fontWeight: 700, color: "#121212", margin: 0 },
  subtitle: { fontSize: 13, color: "#888", marginTop: 2 },

  // Layout dos columnas
  layout: {
    maxWidth: 1340,
    margin: "0 auto",
    display: "grid",
    gridTemplateColumns: "400px 1fr",
    gap: 20,
    alignItems: "start",
  },

  // Panel izquierdo (formulario)
  formCard: {
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 2px 20px rgba(159,0,89,0.08)",
    overflow: "hidden",
  },
  formCardHeader: {
    background: "#9f0059",
    padding: "16px 22px",
  },
  formCardTitle: { color: "#fff", fontSize: 15, fontWeight: 700, margin: 0 },
  formCardSub: { color: "rgba(255,255,255,0.7)", fontSize: 12, marginTop: 2 },
  formBody: { padding: "20px 22px" },

  fieldGroup: { display: "flex", flexDirection: "column", gap: 4, marginBottom: 14 },
  label: { fontSize: 11, fontWeight: 700, color: "#666", letterSpacing: "0.04em", textTransform: "uppercase" },
  input: {
    padding: "8px 11px", border: "1.5px solid #e0d6dc",
    borderRadius: 7, fontSize: 13, color: "#121212",
    background: "#fafafa", outline: "none",
    boxSizing: "border-box", width: "100%",
  },
  textarea: {
    padding: "9px 11px", border: "1.5px solid #e0d6dc",
    borderRadius: 7, fontSize: 13, color: "#121212",
    background: "#fafafa", outline: "none",
    resize: "vertical", minHeight: 80,
    fontFamily: "inherit", boxSizing: "border-box", width: "100%",
  },
  montoBox: {
    background: "#fdf0f6", border: "1.5px solid #f0d6e8",
    borderRadius: 7, padding: "8px 11px",
    fontSize: 15, fontWeight: 700, color: "#9f0059",
  },
  divider: { height: 1, background: "#f0e6ec", margin: "6px 0 14px" },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },

  // Template selector
  templateRow: { display: "flex", alignItems: "center", gap: 6, marginBottom: 6 },
  templateSelect: {
    flex: 1, padding: "6px 8px",
    border: "1.5px solid #e0d6dc", borderRadius: 6,
    fontSize: 12, color: "#555", background: "#fff",
    outline: "none", cursor: "pointer",
  },
  templateBtn: (color) => ({
    display: "flex", alignItems: "center", gap: 3,
    padding: "6px 10px", border: "none", borderRadius: 6,
    fontSize: 11, fontWeight: 700, cursor: "pointer",
    background: color === "save" ? "#f0e6ec" : "transparent",
    color: color === "save" ? "#9f0059" : "#c00",
  }),

  // Logo
  logoCheck: {
    display: "flex", alignItems: "center", gap: 8,
    cursor: "pointer", fontSize: 13, color: "#555", marginBottom: 8,
  },
  logoImg: { maxWidth: 120, maxHeight: 48, objectFit: "contain", borderRadius: 5 },

  formFooter: {
    padding: "14px 22px",
    background: "#fdf0f6",
    borderTop: "1.5px solid #f0e6ec",
    display: "flex", justifyContent: "flex-end", gap: 10,
  },
  btnSecondary: {
    padding: "9px 18px", background: "transparent",
    border: "1.5px solid #c990b0", borderRadius: 8,
    fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#9f0059",
  },
  btnPrimary: {
    padding: "9px 20px", background: "#9f0059",
    border: "none", borderRadius: 8,
    fontSize: 13, fontWeight: 700, cursor: "pointer", color: "#fff",
    display: "flex", alignItems: "center", gap: 6,
    textDecoration: "none",
  },

  // Panel derecho (preview)
  previewCard: {
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 2px 20px rgba(159,0,89,0.08)",
    overflow: "hidden",
    position: "sticky",
    top: 20,
  },
  previewHeader: {
    padding: "12px 20px",
    background: "#f7f4f6",
    borderBottom: "1px solid #ede8ed",
    display: "flex", alignItems: "center", justifyContent: "space-between",
  },
  previewTitle: { fontSize: 13, fontWeight: 700, color: "#555" },
  previewBadge: {
    fontSize: 11, padding: "2px 8px", borderRadius: 20,
    background: "#dcfce7", color: "#16a34a", fontWeight: 600,
  },
};

// ─── Template selector ────────────────────────────────────────────────────────
const TemplateSelector = ({ storageKey, value, onChange }) => {
  const [templates, setTemplates] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey)) || []; }
    catch { return []; }
  });
  const [selected, setSelected] = useState("");

  const saveTemplates = (list) => {
    setTemplates(list);
    localStorage.setItem(storageKey, JSON.stringify(list));
  };
  const handleSelect = (e) => {
    const idx = e.target.value;
    setSelected(idx);
    if (idx !== "") onChange(templates[parseInt(idx)].texto);
  };
  const handleSave = () => {
    if (!value.trim()) return;
    const nombre = window.prompt("Nombre del template:");
    if (!nombre?.trim()) return;
    saveTemplates([...templates, { nombre: nombre.trim(), texto: value }]);
  };
  const handleDelete = () => {
    if (selected === "") return;
    saveTemplates(templates.filter((_, i) => i !== parseInt(selected)));
    setSelected("");
  };

  return (
    <div style={S.templateRow}>
      <select style={S.templateSelect} value={selected} onChange={handleSelect}>
        <option value="">{templates.length === 0 ? "Sin templates guardados" : "Usar template…"}</option>
        {templates.map((t, i) => <option key={i} value={i}>{t.nombre}</option>)}
      </select>
      {selected !== "" && (
        <button style={S.templateBtn("delete")} onClick={handleDelete} title="Eliminar template">
          <MdDelete size={13} /> Eliminar
        </button>
      )}
      <button style={S.templateBtn("save")} onClick={handleSave} title="Guardar como template">
        <MdSave size={13} /> Guardar
      </button>
    </div>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────
function Presupuesto() {
  const location = useLocation();
  const history  = useHistory();
  const { orderId, totalAmount } = location.state || {};

  const [formData, setFormData] = useState({
    fecha:             new Date().toISOString().split("T")[0],
    destinatario:      "",
    textoPresentacion: "",
    condiciones:       "",
    nombreUsuario:     "",
    cargoUsuario:      "",
    monto:             `${totalAmount ?? ""}`,
  });
  const [userId, setUserId] = useState(null);

  const [orderData,      setOrderData]      = useState(null);
  const [usarLogo,       setUsarLogo]       = useState(true);
  const [loadingOrder,   setLoadingOrder]   = useState(!!orderId);

  // Cargar datos del usuario y de la orden
  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      try {
        const parsed = JSON.parse(user);
        setUserId(parsed.id || null);
        setFormData(prev => ({
          ...prev,
          nombreUsuario: parsed.nombre || "",
          cargoUsuario:  parsed.cargo  || "",
        }));
      } catch {}
    }

    if (!orderId) { setLoadingOrder(false); return; }

    fetch(`${BASE}/getOrderDetails.php`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ orderId }),
    })
      .then(r => r.json())
      .then(data => {
        setOrderData(data);
        if (!totalAmount && data.monto_total) {
          setFormData(prev => ({ ...prev, monto: String(data.monto_total) }));
        }
      })
      .catch(() => {})
      .finally(() => setLoadingOrder(false));
  }, [orderId]); // eslint-disable-line

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Persistir cargo en BD cuando cambia
    if (name === "cargoUsuario" && userId) {
      const fd = new FormData();
      fd.append("id", userId);
      fd.append("cargo", value);
      fetch(`${BASE}/actualizar_cargo_usuario.php`, { method: "POST", body: fd }).catch(() => {});
      // Actualizar también el localStorage para que persista entre sesiones
      try {
        const raw = localStorage.getItem("user");
        if (raw) {
          const u = JSON.parse(raw);
          u.cargo = value;
          localStorage.setItem("user", JSON.stringify(u));
        }
      } catch {}
    }
  };

  // Props para el documento PDF (memo para no re-renderizar en cada keystroke)
  const logoTwinpackUrl = logoTwinpackSrc;
  const logoProveedorUrl = usarLogo && orderData?.logo
    ? `${BASE}/uploads_php/logos/${orderData.logo}`
    : null;

  const docProps = useMemo(() => ({
    orderId:           orderId || "—",
    fecha:             formData.fecha,
    destinatario:      formData.destinatario,
    textoPresentacion: formData.textoPresentacion,
    condiciones:       formData.condiciones,
    nombreUsuario:     formData.nombreUsuario,
    cargoUsuario:      formData.cargoUsuario,
    monto:             formData.monto,
    items:             orderData?.items ?? [],
    cliente:           orderData?.cliente ?? "",
    logoTwinpackUrl,
    logoProveedorUrl,
  }), [
    orderId, formData.fecha, formData.destinatario,
    formData.textoPresentacion, formData.condiciones,
    formData.nombreUsuario, formData.cargoUsuario, formData.monto,
    orderData, usarLogo, logoTwinpackUrl, logoProveedorUrl,
  ]);

  const nombreArchivo = `Cotizacion_${orderData?.cliente ?? "Twinpack"}_${formData.fecha}.pdf`;

  return (
    <section style={S.page}>
      {/* Top bar */}
      <div style={S.topBar}>
        <button style={S.backBtn} onClick={() => history.push("/dashboard/cotizaciones")}>
          <FaArrowLeft size={18} />
        </button>
        <div>
          <h2 style={S.title}>Generar Presupuesto</h2>
          <p style={S.subtitle}>Orden nº {orderId}</p>
        </div>
      </div>

      <div style={S.layout}>
        {/* ── Panel izquierdo: formulario ── */}
        <div style={S.formCard}>
          <div style={S.formCardHeader}>
            <p style={S.formCardTitle}>Datos del presupuesto</p>
            <p style={S.formCardSub}>Editá los campos — la preview actualiza en tiempo real</p>
          </div>

          <div style={S.formBody}>
            {/* Monto */}
            <div style={S.fieldGroup}>
              <label style={S.label}>Monto total</label>
              <div style={S.montoBox}>
                ${parseFloat(formData.monto || 0).toLocaleString("es-AR", { minimumFractionDigits: 2 })}
              </div>
            </div>

            <div style={S.grid2}>
              <div style={S.fieldGroup}>
                <label style={S.label}>Fecha</label>
                <input style={S.input} type="date" name="fecha"
                  value={formData.fecha} onChange={handleChange} />
              </div>
              <div style={S.fieldGroup}>
                <label style={S.label}>Nombre usuario</label>
                <input style={S.input} type="text" name="nombreUsuario"
                  placeholder="Tu nombre" value={formData.nombreUsuario} onChange={handleChange} />
              </div>
            </div>
            <div style={S.fieldGroup}>
              <label style={S.label}>Cargo</label>
              <input style={S.input} type="text" name="cargoUsuario"
                placeholder="Ej: Ejecutivo de Cuentas" value={formData.cargoUsuario} onChange={handleChange} />
            </div>

            <div style={S.fieldGroup}>
              <label style={S.label}>Destinatario</label>
              <input style={S.input} type="text" name="destinatario"
                placeholder="Sr/a…" value={formData.destinatario} onChange={handleChange} />
            </div>

            {/* Logo */}
            {orderData?.logo && (
              <>
                <div style={S.divider} />
                <div style={S.fieldGroup}>
                  <label style={S.logoCheck}>
                    <input type="checkbox" checked={usarLogo}
                      onChange={e => setUsarLogo(e.target.checked)}
                      style={{ width: 15, height: 15, accentColor: "#9f0059", cursor: "pointer" }} />
                    Incluir logo del proveedor
                  </label>
                  {usarLogo && (
                    <img
                      src={`${BASE}/uploads_php/logos/${orderData.logo}`}
                      alt="Logo proveedor"
                      style={S.logoImg}
                    />
                  )}
                </div>
              </>
            )}

            <div style={S.divider} />

            {/* Texto de presentación */}
            <div style={S.fieldGroup}>
              <label style={S.label}>Texto de presentación</label>
              <TemplateSelector
                storageKey="presupuesto_templates_presentacion"
                value={formData.textoPresentacion}
                onChange={(txt) => setFormData(prev => ({ ...prev, textoPresentacion: txt }))}
              />
              <textarea style={{ ...S.textarea, minHeight: 70 }}
                name="textoPresentacion"
                placeholder="Texto introductorio…"
                value={formData.textoPresentacion}
                onChange={handleChange}
              />
            </div>

            {/* Condiciones */}
            <div style={S.fieldGroup}>
              <label style={S.label}>Condiciones comerciales</label>
              <TemplateSelector
                storageKey="presupuesto_templates_condiciones"
                value={formData.condiciones}
                onChange={(txt) => setFormData(prev => ({ ...prev, condiciones: txt }))}
              />
              <textarea style={{ ...S.textarea, minHeight: 90 }}
                name="condiciones"
                placeholder="Una condición por línea…"
                value={formData.condiciones}
                onChange={handleChange}
              />
            </div>
          </div>

          <div style={S.formFooter}>
            <button style={S.btnSecondary}
              onClick={() => history.push("/dashboard/cotizaciones")}>
              Cancelar
            </button>
            {!loadingOrder && (
              <PDFDownloadLink
                document={<PresupuestoPDF {...docProps} />}
                fileName={nombreArchivo}
                style={{ textDecoration: "none" }}
              >
                {({ loading }) => (
                  <span style={{ ...S.btnPrimary, opacity: loading ? 0.7 : 1 }}>
                    ⬇ {loading ? "Preparando…" : "Descargar PDF"}
                  </span>
                )}
              </PDFDownloadLink>
            )}
          </div>
        </div>

        {/* ── Panel derecho: preview ── */}
        <div style={S.previewCard}>
          <div style={S.previewHeader}>
            <span style={S.previewTitle}>Vista previa</span>
            <span style={S.previewBadge}>● En vivo</span>
          </div>
          {loadingOrder ? (
            <div style={{ padding: 40, textAlign: "center", color: "#aaa", fontSize: 14 }}>
              Cargando datos de la orden…
            </div>
          ) : (
            <PDFViewer
              width="100%"
              height="780px"
              style={{ border: "none", display: "block" }}
              showToolbar={false}
            >
              <PresupuestoPDF {...docProps} />
            </PDFViewer>
          )}
        </div>
      </div>
    </section>
  );
}

export default Presupuesto;
